import { getAuthorizedFetchOptions } from '../../../helpers/getAuthorizedFetchOptions';
import { ClientInfo, defaultClientInfo } from '../clientInfo';
import { ConversationSession } from '../conversation/types';
import { TriageCase, TriageUser } from '../triage/types';
import { TicketEscalation, TicketPayloadBase, TicketResult, TicketService, TicketSyncPayload } from './types';

/** Matches MaxConversationTranscript in Kois.Api ChatController. */
export const MAX_TICKET_TRANSCRIPT = 200;

export interface BuildTicketPayloadArgs {
  session: ConversationSession;
  user: TriageUser;
  selectedCase?: TriageCase;
  sentiment?: 'frustrated';
  maxTranscript?: number;
  now?: () => number;
  client?: Partial<ClientInfo>;
}

/** Pure: the session as the ticket API receives it (whole transcript, most recent entries when capped). */
export function buildTicketPayload({
  session,
  user,
  selectedCase,
  sentiment,
  maxTranscript = MAX_TICKET_TRANSCRIPT,
  now = Date.now,
  client,
}: BuildTicketPayloadArgs): TicketPayloadBase {
  const recent = session.messages.slice(-maxTranscript);
  const lastWithIntent = [...session.messages].reverse().find((m) => m.intentId);
  return {
    schemaVersion: 1,
    sessionId: session.id,
    createdAt: new Date(now()).toISOString(),
    user,
    selectedCase,
    transcript: recent.map((m) => ({
      role: m.role,
      text: m.text,
      createdAt: new Date(m.createdAt).toISOString(),
      intentId: m.intentId,
      ...(m.feedback ? { feedback: m.feedback } : {}),
    })),
    lastIntentId: lastWithIntent?.intentId,
    ...(sentiment ? { sentiment } : {}),
    client: { ...defaultClientInfo(), ...(client || {}) },
  };
}

export const withTicketAuth = (payload: TicketPayloadBase, token?: string, escalation?: TicketEscalation): TicketSyncPayload => ({
  ...payload,
  ...(token ? { ticketToken: token } : {}),
  ...(escalation ? { escalation } : {}),
});

export interface CreateTicketServiceOptions {
  /** `.../api/chat/conversations`; sync is PUT `<endpoint>/<ref>`, close is POST `<endpoint>/<ref>/close`. */
  endpoint?: string;
  fetchImpl?: typeof fetch;
  logger?: Pick<Console, 'info' | 'warn'>;
}

/** Does nothing and reports `mode: 'noop'`; the engine then keeps no ticket on the session. */
export const noopTicketService: TicketService = {
  async start() { return { accepted: true, mode: 'noop' }; },
  async sync() { return { accepted: true, mode: 'noop' }; },
  async close() { return { accepted: true, mode: 'noop' }; },
};

/**
 * Talks to `REACT_APP_CHAT_TICKET_END_POINT`. With no endpoint configured every call is a no-op, so the
 * chat works unchanged where the API is not deployed. Never throws: failures come back as `{ accepted: false }`.
 */
export function createTicketService({
  endpoint = process.env.REACT_APP_CHAT_TICKET_END_POINT || '',
  fetchImpl,
  logger = console,
}: CreateTicketServiceOptions = {}): TicketService {
  const base = (endpoint || '').trim().replace(/\/+$/, '');
  if (!base) return noopTicketService;
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined);

  const call = async (method: 'POST' | 'PUT', url: string, payload: unknown, what: string): Promise<TicketResult> => {
    if (!doFetch) return { accepted: false, mode: 'remote', error: 'fetch unavailable' };
    try {
      const response = await doFetch(url, getAuthorizedFetchOptions(method, payload) as RequestInit);
      if (!response.ok) {
        if (response.status !== 503) logger.warn(`[askAi] ticket ${what} failed: HTTP ${response.status}`);
        return { accepted: false, mode: 'remote', error: `HTTP ${response.status}` };
      }
      let body: any = null;
      try { body = await response.json(); } catch { /* empty body is fine */ }
      return {
        accepted: true,
        mode: 'remote',
        ticketRef: body?.ticketRef ? String(body.ticketRef) : undefined,
        ticketUrl: body?.ticketUrl ? String(body.ticketUrl) : undefined,
        ticketToken: body?.ticketToken ? String(body.ticketToken) : undefined,
        status: body?.status ? String(body.status) : undefined,
      };
    } catch (error: any) {
      logger.warn(`[askAi] ticket ${what} failed`, error);
      return { accepted: false, mode: 'remote', error: error?.message || 'request failed' };
    }
  };

  return {
    start: (payload) => call('POST', base, payload, 'start'),
    sync: (ref, payload) => call('PUT', `${base}/${encodeURIComponent(ref)}`, payload, 'sync'),
    close: (ref, payload) => call('POST', `${base}/${encodeURIComponent(ref)}/close`, payload, 'close'),
  };
}
